import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import Header from './Header';
import Footer from './Footer';
import ProductSidebar from './ProductSidebar';
import './MockInterviewPage.css';
import { createDoc, PDF, COLOR, fR, sR, T, drawCoverHeader, drawPageHeader, drawFooters, drawSectionHeading } from '../utils/pdfBrand';

// Standalone page for the Mock Interview evaluation report. The live
// session (MockInterviewPage) redirects here once the AI finishes
// scoring, handing over { evaluation, questions, formData } via router
// state. We also stash the payload in sessionStorage so a hard refresh
// on this route keeps the report instead of dropping the user on a
// blank page.
const SESSION_KEY = 'mockInterviewResults';

const getConfidenceMeta = (score) => {
  if (!score && score !== 0) return { color: '#888', label: 'N/A', cls: 'conf-na', gradFrom: '#888', gradTo: '#888' };
  if (score >= 70) return { color: '#4caf50', gradFrom: '#4caf50', gradTo: '#81c784', label: 'High Confidence', cls: 'conf-high' };
  if (score >= 40) return { color: '#ffc107', gradFrom: '#ffa000', gradTo: '#ffc107', label: 'Moderate Confidence', cls: 'conf-mid' };
  return { color: '#ff6b6b', gradFrom: '#f44336', gradTo: '#ff6b6b', label: 'Low Confidence', cls: 'conf-low' };
};

const MockInterviewResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const [, setAuthModal] = useState(false);

  // Resolve the report payload: prefer router state (fresh redirect),
  // fall back to sessionStorage (refresh), persist state → storage.
  const payload = useMemo(() => {
    if (location.state && location.state.evaluation) {
      try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(location.state)); } catch (e) { /* quota — ignore */ }
      return location.state;
    }
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }, [location.state]);

  const evaluation = payload?.evaluation || null;
  const questions = payload?.questions || [];
  const formData = payload?.formData || {};

  // No report to show (direct nav / stale storage) → back to the landing.
  useEffect(() => {
    if (!evaluation) navigate('/mockInterview', { replace: true });
  }, [evaluation, navigate]);

  const downloadReport = () => {
    if (!evaluation) return;

    const doc = createDoc();
    const { H, M, CW } = PDF;
    let y = 0;

    const scores = evaluation.questionScores || [];
    const avgConfidence = scores.length
      ? Math.round(scores.reduce((s, r) => s + (r.confidenceScore || 0), 0) / scores.length)
      : 0;

    // Muted, professional severity palette — no alarming pure reds/greens.
    const scoreTone = (s) => s >= 70 ? [21, 128, 61] : s >= 50 ? [176, 118, 20] : [188, 66, 66];
    const confTone = (s) => (s || 0) >= 70 ? [21, 128, 61] : (s || 0) >= 40 ? [176, 118, 20] : [188, 66, 66];
    const vDivider = (x, y1, y2) => { doc.setDrawColor(...COLOR.hairline); doc.setLineWidth(0.3); doc.line(x, y1, x, y2); };

    const newPage = () => {
      doc.addPage();
      drawPageHeader(doc, formData.jobRole || 'Mock Interview Report');
      y = 26;
    };
    const checkBreak = (need) => { if (y + need > H - 20) newPage(); };

    const expStr = formData.experienceLevel
      ? formData.experienceLevel.charAt(0).toUpperCase() + formData.experienceLevel.slice(1) + ' Level'
      : '';
    const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    // ── COVER ──
    drawCoverHeader(doc, {
      reportTitle: 'Mock Interview Performance Report',
      role: formData.jobRole || 'Mock Interview',
      company: formData.company,
      dateStr,
    });
    if (expStr) T(doc, COLOR.muted, 9, false, expStr, M, formData.company ? 91 : 84);

    // ── SUMMARY (three-column stat band, hairline dividers) ──
    y = 106;
    const bandH = 26;
    fR(doc, 250, 250, 253, M, y, CW, bandH);
    sR(doc, ...COLOR.hairline, 0.3, M, y, CW, bandH);
    const col = CW / 3;
    [
      { num: `${evaluation.overallScore}%`, label: 'Overall Score', sub: evaluation.overallScore >= 70 ? 'Strong' : evaluation.overallScore >= 50 ? 'Average' : 'Needs Work', tone: scoreTone(evaluation.overallScore) },
      { num: `${avgConfidence}%`, label: 'Avg Confidence', sub: avgConfidence >= 70 ? 'High' : avgConfidence >= 40 ? 'Moderate' : 'Low', tone: confTone(avgConfidence) },
      { num: `${scores.length}`, label: 'Questions', sub: 'Evaluated', tone: COLOR.accent },
    ].forEach((s, i) => {
      const cx = M + i * col + col / 2;
      if (i > 0) vDivider(M + i * col, y + 5, y + bandH - 5);
      T(doc, s.tone, 19, true, s.num, cx, y + 13, { align: 'center' });
      doc.setCharSpace(0.5);
      T(doc, COLOR.muted, 6.5, true, s.label.toUpperCase(), cx, y + 19, { align: 'center' });
      doc.setCharSpace(0);
      T(doc, [150, 150, 165], 6.5, false, s.sub, cx, y + 23.5, { align: 'center' });
    });
    y += bandH + 12;

    // ── READINESS BAR ──
    doc.setCharSpace(0.5);
    T(doc, COLOR.muted, 7, true, 'INTERVIEW READINESS', M, y);
    doc.setCharSpace(0);
    T(doc, scoreTone(evaluation.overallScore), 8, true, `${evaluation.overallScore}%`, M + CW, y, { align: 'right' });
    y += 3;
    fR(doc, 232, 230, 240, M, y, CW, 2.4);
    fR(doc, ...scoreTone(evaluation.overallScore), M, y, CW * (evaluation.overallScore / 100), 2.4);
    y += 14;

    // ── GENERAL FEEDBACK ──
    const fbLines = doc.splitTextToSize(evaluation.generalFeedback || '', CW - 16);
    const fbH = fbLines.length * 4.8 + 15;
    checkBreak(fbH + 6);
    fR(doc, 249, 248, 253, M, y, CW, fbH);
    sR(doc, ...COLOR.hairline, 0.3, M, y, CW, fbH);
    fR(doc, ...COLOR.accent, M, y, 1.6, fbH);
    doc.setCharSpace(0.5);
    T(doc, COLOR.accent, 7, true, 'GENERAL FEEDBACK', M + 8, y + 7);
    doc.setCharSpace(0);
    T(doc, COLOR.text, 8.5, false, fbLines, M + 8, y + 13);
    y += fbH + 14;

    // ── SECTION HEADING ──
    checkBreak(16);
    y = drawSectionHeading(doc, `Question Breakdown  ·  ${scores.length} Questions`, y);
    y += 2;

    // ── PER QUESTION ──
    scores.forEach((result, index) => {
      const q = questions[index];
      const pace = result.pace || 0;
      const paceLabel = pace > 160 ? 'Fast' : pace > 110 ? 'Good' : pace > 0 ? 'Slow' : '—';
      const fc = result.fillerWords || 0;
      const fcLabel = fc > 5 ? 'Too Many' : fc > 2 ? 'A Few' : 'Clean';
      const fcTone = fc > 5 ? [188, 66, 66] : fc > 2 ? [176, 118, 20] : [21, 128, 61];
      const qTone = scoreTone(result.score);
      const cTone = confTone(result.confidenceScore);
      const cLabel = getConfidenceMeta(result.confidenceScore).label;

      const ansLinesAll = doc.splitTextToSize(q?.expectedAnswer || '', CW - 16);
      const ansShown = ansLinesAll.slice(0, 6);
      const advLinesAll = doc.splitTextToSize(result.advice || '', CW - 16);
      checkBreak(56);

      // Question header row (light, accent Q number, colored score)
      const hH = 11;
      fR(doc, 247, 247, 251, M, y, CW, hH);
      fR(doc, ...qTone, M, y, 1.6, hH);
      T(doc, COLOR.accent, 8, true, `Q${String(index + 1).padStart(2, '0')}`, M + 6, y + 7);
      const qFull = q?.question || '';
      const qShort = qFull.length > 88 ? qFull.slice(0, 85) + '…' : qFull;
      T(doc, COLOR.ink, 8.5, false, qShort, M + 17, y + 7);
      T(doc, qTone, 9, true, `${result.score}/100`, M + CW - 4, y + 7, { align: 'right' });
      y += hH + 4;

      // Metrics — 4 columns, hairline separators, no filled color blocks
      const mH = 17;
      fR(doc, 251, 251, 254, M, y, CW, mH);
      sR(doc, ...COLOR.hairline, 0.3, M, y, CW, mH);
      const mcol = CW / 4;
      [
        { val: `${result.contentScore}/50`, label: 'Content', tone: scoreTone((result.contentScore / 50) * 100) },
        { val: `${result.authenticityScore}/50`, label: 'Authenticity', tone: scoreTone((result.authenticityScore / 50) * 100) },
        { val: pace > 0 ? `${pace} wpm` : '—', label: 'Speaking Pace', sub: paceLabel, tone: COLOR.ink },
        { val: `${fc}`, label: 'Filler Words', sub: fcLabel, tone: fcTone },
      ].forEach((m, mi) => {
        const cx = M + mi * mcol + mcol / 2;
        if (mi > 0) vDivider(M + mi * mcol, y + 3, y + mH - 3);
        T(doc, m.tone, 11, true, m.val, cx, y + 7.5, { align: 'center' });
        doc.setCharSpace(0.4);
        T(doc, COLOR.muted, 6, true, m.label.toUpperCase(), cx, y + 12, { align: 'center' });
        doc.setCharSpace(0);
        if (m.sub) T(doc, [150, 150, 165], 5.5, false, m.sub, cx, y + 15, { align: 'center' });
      });
      y += mH + 4;

      // Confidence line + delivery tag
      checkBreak(9);
      T(doc, COLOR.muted, 6.5, true, 'CONFIDENCE', M, y + 3);
      const barX = M + 28, barW = CW * 0.32;
      fR(doc, 232, 230, 240, barX, y + 1, barW, 2.2);
      fR(doc, ...cTone, barX, y + 1, barW * ((result.confidenceScore || 0) / 100), 2.2);
      T(doc, cTone, 7.5, true, `${result.confidenceScore ?? '—'}%`, barX + barW + 4, y + 3);
      T(doc, COLOR.muted, 7, false, cLabel, barX + barW + 16, y + 3);
      const delText = result.isReading ? 'Reading Detected' : 'Authentic Delivery';
      const delTone = result.isReading ? [176, 118, 20] : [21, 128, 61];
      T(doc, delTone, 7, true, delText, M + CW, y + 3, { align: 'right' });
      y += 9;

      // Model answer box (subtle blue tint, thin accent edge)
      checkBreak(ansShown.length * 4.6 + 14);
      const ansH = ansShown.length * 4.6 + 12;
      fR(doc, 248, 250, 254, M, y, CW, ansH);
      sR(doc, ...COLOR.hairline, 0.3, M, y, CW, ansH);
      fR(doc, 79, 110, 200, M, y, 1.6, ansH);
      doc.setCharSpace(0.5);
      T(doc, [55, 80, 150], 6.5, true, 'MODEL ANSWER', M + 8, y + 6);
      doc.setCharSpace(0);
      T(doc, COLOR.text, 8, false, ansShown, M + 8, y + 11);
      if (ansLinesAll.length > ansShown.length) T(doc, [150, 160, 190], 6, false, '… full answer available in the app', M + 8, y + ansH - 2.5);
      y += ansH + 4;

      // Advice box (subtle amber tint)
      if (result.advice) {
        checkBreak(advLinesAll.length * 4.6 + 14);
        const advH = advLinesAll.length * 4.6 + 12;
        fR(doc, 253, 251, 245, M, y, CW, advH);
        sR(doc, ...COLOR.hairline, 0.3, M, y, CW, advH);
        fR(doc, 200, 150, 40, M, y, 1.6, advH);
        doc.setCharSpace(0.5);
        T(doc, [150, 110, 20], 6.5, true, 'HOW TO IMPROVE', M + 8, y + 6);
        doc.setCharSpace(0);
        T(doc, COLOR.text, 8, false, advLinesAll, M + 8, y + 11);
        y += advH + 10;
      } else {
        y += 6;
      }
    });

    drawFooters(doc);

    const filename = `terview-${(formData.jobRole || 'interview').replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(filename);
  };

  if (!evaluation) return null;

  const avgConfidence = evaluation.questionScores.length > 0
    ? Math.round(evaluation.questionScores.reduce((s, r) => s + (r.confidenceScore || 0), 0) / evaluation.questionScores.length)
    : 0;
  const answeredCount = evaluation.questionScores.filter(r => r.score > 0 || r.contentScore > 0).length;
  const confMeta = getConfidenceMeta(avgConfidence);

  return (
    <div className="neural-glass-homepage">
      <div className="neural-background"></div>
      <div className="geometric-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
      </div>

      <Header
        onNavigate={(path) => navigate(path)}
      />
      <ProductSidebar user={user} setShowAuthModal={setAuthModal} />

      <main className="mock-interview-main mock-results-main">
        <div className="evaluation-results">
          <div className="results-header glass">
            <h2>Mock Interview Results</h2>
            <div className="results-summary-row">
              <div className="summary-stat">
                <span className="summary-stat-label">Overall Score</span>
                <span className="summary-stat-value score-gradient">{evaluation.overallScore}%</span>
              </div>
              <div className="summary-stat-divider" />
              <div className="summary-stat">
                <span className="summary-stat-label">Avg Confidence</span>
                <span className={`summary-stat-value ${confMeta.cls}`}>{avgConfidence}%</span>
              </div>
              <div className="summary-stat-divider" />
              <div className="summary-stat">
                <span className="summary-stat-label">Answered</span>
                <span className="summary-stat-value">{answeredCount}/{evaluation.questionScores.length}</span>
              </div>
            </div>
            <div className="overall-confidence-bar-wrap">
              <div className="overall-confidence-bar-track">
                <div className="overall-confidence-bar-fill" style={{ width: `${evaluation.overallScore}%` }} />
              </div>
              <span className="overall-bar-label">Readiness Score</span>
            </div>
            <div className="results-header-actions">
              <button onClick={downloadReport} className="download-report-btn">
                ⬇ Download PDF Report
              </button>
              <button onClick={() => navigate('/dashboard')} className="cta-button results-dash-btn">
                Back to Dashboard
              </button>
            </div>
          </div>

          <div className="general-feedback glass">
            <h3>General Feedback</h3>
            <p>{evaluation.generalFeedback}</p>
          </div>

          <div className="questions-evaluation">
            {evaluation.questionScores.map((result, index) => {
              const cm = getConfidenceMeta(result.confidenceScore);
              const fillerCount = result.fillerWords || 0;
              const pace = result.pace || 0;
              const paceLabel = pace > 160 ? 'Fast' : pace > 110 ? 'Good' : pace > 0 ? 'Slow' : '—';
              const paceCls = pace > 160 ? 'pace-fast' : pace > 110 ? 'pace-good' : pace > 0 ? 'pace-slow' : '';
              return (
                <div key={index} className="question-result glass">
                  <div className="question-header">
                    <div className="qr-title-row">
                      <span className="qr-num">Q{index + 1}</span>
                      <span className="qr-question-text">{questions[index]?.question}</span>
                    </div>
                    <span className={`score-badge ${result.score >= 70 ? 'good' : result.score >= 50 ? 'medium' : 'low'}`}>
                      {result.score}/100
                    </span>
                  </div>

                  <div className="expected-answer">
                    <strong>Model Answer</strong>
                    <p>{questions[index]?.expectedAnswer}</p>
                  </div>

                  <div className="metrics-grid">
                    <div className="metric-box">
                      <span className="metric-label">Content</span>
                      <span className="metric-value">{result.contentScore}<span className="metric-max">/50</span></span>
                      <div className="metric-mini-bar">
                        <div className="metric-mini-fill" style={{ width: `${(result.contentScore / 50) * 100}%`, background: '#667eea' }} />
                      </div>
                    </div>
                    <div className="metric-box">
                      <span className="metric-label">Authenticity</span>
                      <span className="metric-value">{result.authenticityScore}<span className="metric-max">/50</span></span>
                      <div className="metric-mini-bar">
                        <div className="metric-mini-fill" style={{ width: `${(result.authenticityScore / 50) * 100}%`, background: '#e0a3ff' }} />
                      </div>
                    </div>
                    <div className="metric-box">
                      <span className="metric-label">Speaking Pace</span>
                      <span className={`metric-value ${paceCls}`}>{pace > 0 ? pace : '—'}<span className="metric-max">{pace > 0 ? ' wpm' : ''}</span></span>
                      {pace > 0 && <span className={`pace-tag ${paceCls}`}>{paceLabel}</span>}
                    </div>
                    <div className="metric-box">
                      <span className="metric-label">Filler Words</span>
                      <span className={`metric-value ${fillerCount > 5 ? 'metric-warn' : fillerCount > 2 ? 'metric-caution' : 'metric-ok'}`}>{fillerCount}</span>
                      <span className={`filler-tag ${fillerCount > 5 ? 'filler-high' : fillerCount > 2 ? 'filler-mid' : 'filler-low'}`}>
                        {fillerCount > 5 ? 'Too Many' : fillerCount > 2 ? 'A Few' : 'Clean'}
                      </span>
                    </div>
                  </div>

                  <div className="confidence-section">
                    <div className="confidence-header-row">
                      <span className="confidence-label">Confidence Level</span>
                      <div className="confidence-right">
                        <span className={`confidence-pct ${cm.cls}`}>{result.confidenceScore ?? '—'}%</span>
                        <span className={`confidence-tag ${cm.cls}`}>{cm.label}</span>
                        <span className={`delivery-badge ${result.isReading ? 'badge-reading' : 'badge-authentic'}`}>
                          {result.isReading ? '📖 Reading Detected' : '🎤 Authentic Delivery'}
                        </span>
                      </div>
                    </div>
                    <div className="confidence-bar-track">
                      <div
                        className="confidence-bar-fill"
                        style={{
                          width: `${result.confidenceScore ?? 0}%`,
                          background: `linear-gradient(90deg, ${cm.gradFrom}, ${cm.gradTo})`
                        }}
                      />
                    </div>
                  </div>

                  <div className="improvement-advice">
                    <strong>Advice</strong>
                    <p>{result.advice}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default MockInterviewResults;
